import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { connectToBrand_api } from 'api/brandApi';
import useBrand, { BrandExistsState } from './useBrand';
import { CurrentBrand, TikTokAdAccount, TikTokBusinessCenter, TikTokSparkAd } from 'modules/Types';
import { getTikTokAdAccounts_api, createTikTokAdAccount_api, authorizeTikTokCreative_api, CreateTikTokAdAccountParams } from 'api/tiktokApi';
import Axios from 'axios';
import { getConnectedTikTokAdAccount_api } from 'api/tiktokApi';
const isDev = false;

/** Interface for TikTok user data */
interface TikTokUser {
    /** TikTok user's display name */
    display_name: string;
    /** URL of user's avatar image */
    avatar_url?: string;
    /** TikTok user's core ID */
    core_user_id: string;
    /** TikTok user's email */
    email?: string;
}

/** Interface for TikTok connection state */
interface TikTokConnectionState {
    /** Whether login check has been performed */
    hasCheckedLoginStatus: boolean;
    /** Whether user is currently logged in */
    isLoggedIn: boolean;
    /** Whether login is in progress */
    isLoggingIn: boolean;
    /** Whether logout is in progress */
    isLoggingOut: boolean;
    /** Currently logged in TikTok user */
    loggedInUser: TikTokUser | null;

    /** TikTok Business Centers available to the user */
    tiktokBusinessCenters: TikTokBusinessCenter[] | null;
    /** Selected TikTok Ad Account */
    tiktokAdAccount: TikTokAdAccount | null;
    /** Whether the Ad Account is funded */
    tiktokAdAccountFunded: boolean;
    /** Whether TikTok connections are confirmed */
    tiktokConnectionsConfirmed: boolean;
    /** Whether refresh operation is in progress */
    isRefreshing: boolean;

    /** Whether ad accounts are being loaded */
    isLoadingAdAccounts: boolean;

    /** Whether ad account creation is in progress */
    isCreatingAdAccount: boolean;

    /** Whether spark code validation is in progress */
    isValidatingSparkCode: boolean;

    /** Methods */
    /** Initiates TikTok login flow */
    login: () => Promise<void>;
    /** Handles TikTok logout flow */
    logout: () => Promise<void>;
    /** Checks if user is logged into TikTok */
    initializeTikTok: (brand?: CurrentBrand) => Promise<boolean>;
    /** Updates the logged in TikTok user */
    setLoggedInUser: (user: TikTokUser | null) => void;

    /** Refreshes TikTok Ad Account and Business Center data */
    refreshTikTokAdAccount: (autoTransferBCBalance?: boolean) => Promise<{ tiktokBusinessCenters: TikTokBusinessCenter[] | undefined }>;
    /** Sets the TikTok Ad Account */
    setTiktokAdAccount: (account: TikTokAdAccount | null) => Promise<CurrentBrand | null>;

    /** Sets whether the Ad Account is funded */
    setTiktokAdAccountFunded: (funded: boolean) => void;
    /** Sets whether TikTok connections are confirmed */
    setTiktokConnectionsConfirmed: (confirmed: boolean) => void;

    /** Fetches available TikTok Ad Accounts */
    getTikTokAdAccounts: () => Promise<TikTokAdAccount[]>;
    /** Creates a new TikTok ad account */
    createTikTokAdAccount: (params: CreateTikTokAdAccountParams) => Promise<TikTokAdAccount | null>;

    /** Validates TikTok spark codes and returns creative details */
    validateSparkCode: (params: {
        /** The spark code to validate */
        sparkCode: string;
        /** Whether the spark code is being validated for express setup */
        isUsingExpressSetup: boolean;
    }) => Promise<TikTokSparkAd>;
}

let DEV_VALUES: {
    tiktok_ad_account_funded: boolean;
    tiktok_ad_account: TikTokAdAccount;
    tiktok_user: TikTokUser;
    tiktok_auth: {
        scope: number[];
        connected: boolean;
        access_token: string;
        advertiser_ids: string[];
    }
} = {
    "tiktok_ad_account_funded": true,
    "tiktok_ad_account": {
        "name": "SymphonyOS - Ads 2",
        "role": "ROLE_ADVERTISER",
        "email": "m*****@*********",
        "status": "STATUS_ENABLE",
        "balance": 50,
        "company": "SymphonyOS Inc.",
        "country": "US",
        "currency": "USD",
        "industry": "292801",
        "language": "en",
        "timezone": "Etc/GMT+8",
        "contacter": "Me*********ia",
        "license_no": "",
        "description": "https://symphony.to",
        "owner_bc_id": "7100040671284822017",
        "advertiser_id": "7426379634167676929",
        "promotion_area": "0",
        "advertiser_name": "SymphonyOS - Ads 2",
        "cellphone_number": "425*****75",
        "display_timezone": "America/Los_Angeles",
        "license_province": undefined,
        "rejection_reason": undefined,
        "telephone_number": "425*****75",
        "promotion_center_city": undefined,
        "advertiser_account_type": "AUCTION",
        "promotion_center_province": undefined
    },
    "tiktok_user": {
        "email": "m***h@symphony.to",
        "avatar_url": "",
        "core_user_id": "7099797735969604609",
        "display_name": "Megh"
    },
    tiktok_auth: {
        "scope": [
            15000000,
            1,
            2,
            3,
            4,
            16000000,
            6,
            20000000,
            8,
            7050363942434013000,
            10000
        ],
        "connected": true,
        "access_token": "231f663ad94ab08e52bef60813a14cd6ddedddaf",
        "advertiser_ids": [
            "7100029029427724290",
            "7100040947534266370",
            "7287357624280989698",
            "7426376720917200897",
            "7426379356148269073",
            "7426379534095663121",
            "7426379634167676929",
            "7426380006986694657",
            "7428769122223144961"
        ]
    }
}

/**
 * Custom hook for managing TikTok connections and authentication state
 * Provides methods for login, logout, and managing TikTok ad accounts
 */
export const useTikTokConnections = create(immer<TikTokConnectionState>((set, get) => ({
    // Initial state
    hasCheckedLoginStatus: false,
    isLoggedIn: isDev ? true : false,
    isLoggingIn: false,
    isLoggingOut: false,
    loggedInUser: isDev ? DEV_VALUES.tiktok_user : null,
    tiktokAdAccount: isDev ? DEV_VALUES.tiktok_ad_account : null,
    tiktokAdAccountFunded: isDev ? DEV_VALUES.tiktok_ad_account_funded : false,
    tiktokConnectionsConfirmed: isDev ? true : false,
    isRefreshing: false,
    isLoadingAdAccounts: false,
    isCreatingAdAccount: false,
    isValidatingSparkCode: false,
    tiktokBusinessCenters: [],

    // Methods
    /**
     * Sets the currently logged in TikTok user
     * @param user - TikTok user object or null when logging out
     */
    setLoggedInUser: (user: TikTokUser | null) => set(state => {
        state.loggedInUser = user;
    }),

    /**
     * Initiates the TikTok login flow by opening a popup window
     * Monitors the window state and updates login status accordingly
     */
    login: async () => {
        try {
            const { currentBrand } = useBrand.getState() as BrandExistsState;
            set(state => { state.isLoggingIn = true });

            const width = 600;
            const height = 700;
            const left = window.screen.width / 2 - width / 2;
            const top = window.screen.height / 2 - height / 2;

            const redirectUri = encodeURIComponent(`${window.location.origin}/tiktok/callback`);
            const state = encodeURIComponent(JSON.stringify({
                brandId: currentBrand?.id,
                test: 'megh'
            }));
            const tiktokAuthUrl = `https://business-api.tiktok.com/portal/auth?app_id=7190203583793463298&state=${state}&redirect_uri=${redirectUri}`;

            const loginWindow = window.open(
                tiktokAuthUrl,
                'TikTok Login',
                `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes`
            );

            // Check if window was closed
            const checkWindowClosed = setInterval(() => {
                if (loginWindow?.closed) {
                    clearInterval(checkWindowClosed);
                    set(state => { state.isLoggingIn = false });
                    get().initializeTikTok();
                }
            }, 1000);

        } catch (error) {
            console.error('Error logging in to TikTok:', error);
            set(state => { state.isLoggingIn = false });
        }
    },

    initializeTikTok: async (brand?: CurrentBrand): Promise<boolean> => {
        try {
            let mainBrand = brand || (useBrand.getState().currentBrand as CurrentBrand);

            const {
                tiktok_auth,
                tiktok_user
            } = mainBrand.connections

            if (tiktok_auth && tiktok_user) {
                // TODO: Add token validation API call here
                set(state => {
                    state.hasCheckedLoginStatus = true;
                    state.isLoggedIn = true;
                });
                return true;
            }

            set(state => {
                state.hasCheckedLoginStatus = true;
                state.isLoggedIn = false;
            });
            return false;

        } catch (error) {
            console.error('Error checking TikTok login status:', error);
            set(state => {
                state.hasCheckedLoginStatus = true;
                state.isLoggedIn = false;
            });
            return false;
        }
    },

    logout: async () => {
        try {
            set(state => { state.isLoggingOut = true });

            // TODO: Add TikTok logout API call here if needed

            set(state => {
                state.isLoggedIn = false;
                state.loggedInUser = null;
            });

        } catch (error) {
            console.error('Error logging out of TikTok:', error);
        } finally {
            set(state => { state.isLoggingOut = false });
        }
    },

    /**
     * Updates the selected TikTok Ad Account
     * @param account - TikTok ad account object or null to clear selection
     */
    setTiktokAdAccount: async (account: TikTokAdAccount | null) => {

        const { currentBrand } = useBrand.getState() as BrandExistsState;
        try {
            const updatedBrand = await connectToBrand_api({
                brandId: Number(currentBrand?.id),
                brandSlug: String(currentBrand?.slug),
                service: 'tiktok_ad_account',
                value: account,
            });

            set(state => {
                state.tiktokAdAccount = account;
            });

            return updatedBrand;
        } catch (error) {
            console.error('Error connecting TikTok ad account:', error);
            throw error;
        }
    },

    /**
     * Updates the funded status of the selected TikTok Ad Account
     * @param funded - Boolean indicating if the account has sufficient funds
     */
    setTiktokAdAccountFunded: (funded: boolean) => set(state => {
        state.tiktokAdAccountFunded = funded;
    }),

    /**
     * Updates the confirmation status of TikTok connections
     * @param confirmed - Boolean indicating if all required connections are confirmed
     */
    setTiktokConnectionsConfirmed: (confirmed: boolean) => set(state => {
        state.tiktokConnectionsConfirmed = confirmed;
    }),

    refreshTikTokAdAccount: async (autoTransferBCBalance?: boolean) => {
        try {
            set(state => { state.isRefreshing = true });
            const { currentBrand } = useBrand.getState() as BrandExistsState;

            if (!currentBrand?.id) {
                throw new Error('No current brand found');
            }

            const data = await getConnectedTikTokAdAccount_api(
                currentBrand.id,
                'current' // Assuming we're getting the currently connected account
            );

            if (data) {
                set(state => {
                    if (data.tiktok_user) state.loggedInUser = data.tiktok_user;
                    if (data.tiktok_business_centers) {
                        state.tiktokBusinessCenters = data.tiktok_business_centers;
                    }

                    if (data.connected && data.tiktok_ad_account) {
                        state.tiktokAdAccount = data.tiktok_ad_account;
                        state.tiktokAdAccountFunded = data.tiktok_ad_account_funded;
                        state.tiktokConnectionsConfirmed = true;
                    } else {
                        state.tiktokAdAccount = null;
                        state.tiktokAdAccountFunded = false;
                        state.tiktokConnectionsConfirmed = false;
                    }
                });

                return { tiktokBusinessCenters: data.tiktok_business_centers };
            }

            return { tiktokBusinessCenters: undefined };
        } catch (error) {
            console.error('Error fetching TikTok ad account:', error);
            set(state => {
                state.tiktokAdAccount = null;
                state.tiktokConnectionsConfirmed = false;
            });
            return { tiktokBusinessCenters: undefined };
        } finally {
            set(state => { state.isRefreshing = false });
        }
    },

    getTikTokAdAccounts: async () => {
        try {
            set(state => { state.isLoadingAdAccounts = true });
            const { currentBrand } = useBrand.getState() as BrandExistsState;

            if (!currentBrand?.id) {
                throw new Error('No current brand found');
            }

            const adAccounts = await getTikTokAdAccounts_api(currentBrand.id);

            return adAccounts;
        } catch (error) {
            console.error('Failed to fetch TikTok ad accounts:', error);
            return [];
        } finally {
            set(state => { state.isLoadingAdAccounts = false });
        }
    },

    createTikTokAdAccount: async (params: CreateTikTokAdAccountParams) => {
        try {
            set(state => { state.isCreatingAdAccount = true });
            const { currentBrand } = useBrand.getState() as BrandExistsState;

            const { data, error } = await createTikTokAdAccount_api(currentBrand?.id, params);

            if (error) {
                throw error;
            }

            if (data) {
                // Update the ad accounts list with the new account
                set(state => {
                    state.tiktokAdAccount = data;
                });
            }

            return data;
        } catch (error) {
            console.error('Failed to create TikTok ad account:', error);
            throw error;
        } finally {
            set(state => { state.isCreatingAdAccount = false });
        }
    },

    /** 
     * Validates a TikTok Spark Code and authorizes it for use in ads
     * @param sparkCode - The Spark Code to validate
     * @returns The validated Spark Code data from the API
     * @throws Error if validation fails
     */
    validateSparkCode: async ({
        sparkCode,
        isUsingExpressSetup
    }: { sparkCode: string, isUsingExpressSetup: boolean }): Promise<TikTokSparkAd> => {
        try {
            set(state => { state.isValidatingSparkCode = true });
            const { currentBrand } = useBrand.getState() as BrandExistsState;

            const response = await authorizeTikTokCreative_api(currentBrand?.id, {
                tiktokSparkCode: sparkCode,
                isExpressSetup: isUsingExpressSetup
            });

            return response;
        } catch (error) {
            console.error('Error authorizing TikTok creative:', error);
            throw error;
        } finally {
            set(state => { state.isValidatingSparkCode = false });
        }
    }
}))
);

export default useTikTokConnections;